import { TrashIcon } from '@heroicons/react/solid'
import { FieldArray, Field, useFormikContext } from 'formik'
import { string } from 'yup'

import { FormValues } from '.'
import { Link } from '@components/Link'
import { Tooltip } from '@components/UI/Tooltip'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'
import { Select, Option } from '@components/UI/Form/Select'

export function Sharing() {
  const { errors, values, setFieldValue } = useFormikContext<FormValues>()

  return (
    <div className="block p-8 bg-white rounded shadow-sm" id="sharingTargets">
      <h2 className="mb-8 text-2xl font-black tracking-tight">
        Share Your Domoreport{' '}
        <span className="font-semibold text-gray-300 text-l">(Optional)</span>
      </h2>
      <p>
        You can share this Domoreport with additional people by providing their
        email below.{' '}
        <Link
          type="outbound"
          href="https://intercom.help/majordomo/en/articles/3216497-how-do-i-share-my-domoreport"
        >
          Learn more
        </Link>
      </p>
      <div className="divide-y divide-gray-100">
        <FieldArray name="sharingTargets">
          {helpers => (
            <>
              {values.sharingTargets.map((target, index) => (
                <div
                  key={index}
                  className="flex flex-col py-4 space-y-4 md:flex-row md:space-x-8 md:space-y-0 justify-stretch md:justify-between"
                >
                  <div className="flex flex-col md:w-1/2">
                    <Label htmlFor={`sharingTargets.${index}.email`}>
                      Email
                    </Label>
                    <Field
                      component={Input}
                      data-testid={`sharingTargets.${index}.email`}
                      id={`sharingTargets.${index}.email`}
                      name={`sharingTargets.${index}.email`}
                      placeholder="firstlast@example.com"
                      validate={async (email: string) => {
                        if (email) {
                          try {
                            await string().email().validate(email)
                          } catch {
                            return 'Please provide a valid email address'
                          }
                        }
                      }}
                    />
                  </div>
                  <div className="flex flex-col md:w-1/2">
                    <Label htmlFor={`sharingTargets.${index}.role`}>Role</Label>
                    <div className="flex items-center space-x-6">
                      <div className="w-full">
                        <Field
                          component={Select}
                          data-testid={`sharingTargets.${index}.role`}
                          id={`sharingTargets.${index}.role`}
                          name={`sharingTargets.${index}.role`}
                          value={target.role}
                          onChange={(role: string) =>
                            setFieldValue(`sharingTargets.${index}.role`, role)
                          }
                          className="w-full"
                        >
                          <Option value="OWNER">Owner</Option>
                          <Option value="CONTRIBUTOR">Contributor</Option>
                          <Option value="VIEWER">Viewer</Option>
                        </Field>
                      </div>

                      {values.sharingTargets.length > 1 && (
                        <Tooltip label="Remove">
                          <a
                            onClick={() => helpers.remove(index)}
                            data-testid={`sharingTargets.${index}.delete`}
                            className="flex items-center justify-center w-12 h-12 text-gray-400 rounded-md transition-colors hover:bg-red-200 hover:text-red-400 cursor-pointer"
                          >
                            <TrashIcon width={24} height={24} />
                          </a>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <a
                onClick={() =>
                  helpers.push({
                    email: '',
                    role: 'OWNER'
                  })
                }
                className={`block mt-2 ml-auto opacity-50 w-max text-brand transition-opacity hover:underline hover:opacity-100 ${
                  errors.sharingTargets?.length ??
                  values.sharingTargets[values.sharingTargets.length - 1].email
                    .length === 0
                    ? 'hidden'
                    : ''
                }`}
              >
                + Add another person
              </a>
            </>
          )}
        </FieldArray>
      </div>
    </div>
  )
}
